import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import {
  IconSettings,
  IconChevronDown,
  IconChevronUp,
  IconLink,
  IconCopy,
  IconCheck,
  IconUsers,
  IconShieldCheck,
  IconUserOff,
} from '@tabler/icons-react';
import api from '@/services/api';

interface Guest {
  id: string;
  firstName?: string;
  lastName?: string;
  person?: {
    firstName?: string;
    lastName?: string;
    documentNumber?: string;
  };
  sendToMossos: boolean;
  isActive: boolean;
}

interface GuestPortalSectionProps {
  reservationId: string;
  guests: Guest[];
  accessToken?: {
    id: string;
    tokenHash: string;
    status: string;
    portalConfig?: string;
  };
  totalGuests: number;
  onGuestsUpdate: () => void;
}

export function GuestPortalSection({
  reservationId,
  guests,
  accessToken,
  totalGuests,
  onGuestsUpdate,
}: GuestPortalSectionProps) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [minGuests, setMinGuests] = useState(1);
  const [maxGuests, setMaxGuests] = useState<number | null>(totalGuests);

  // Carregar valors inicials des del portalConfig guardat
  useEffect(() => {
    if (accessToken?.portalConfig) {
      try {
        const config = typeof accessToken.portalConfig === 'string' 
          ? JSON.parse(accessToken.portalConfig) 
          : accessToken.portalConfig;
        if (config.minGuests !== undefined) setMinGuests(config.minGuests);
        if (config.maxGuests !== undefined) setMaxGuests(config.maxGuests);
      } catch (e) {
        console.error('Error parsing portalConfig:', e);
      }
    }
  }, [accessToken?.portalConfig]);

  // Update guest flags mutation
  const updateGuestFlagsMutation = useMutation({
    mutationFn: async ({
      guestId,
      flags,
    }: {
      guestId: string;
      flags: { sendToMossos?: boolean; isActive?: boolean };
    }) => {
      const response = await api.put(`/guest-portal/guests/${guestId}/flags`, flags);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservation-guests', reservationId] });
      toast.success('Flags actualitzats');
      onGuestsUpdate();
    },
    onError: () => {
      toast.error('Error actualitzant flags');
    },
  });

  // Update token config mutation
  const updateTokenConfigMutation = useMutation({
    mutationFn: async (config: { minGuests: number; maxGuests: number | null }) => {
      if (!accessToken?.id) throw new Error('No access token');
      const response = await api.put(`/guest-portal/tokens/${accessToken.id}/config`, {
        portalConfig: config,
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidar queries per refrescar les dades
      queryClient.invalidateQueries({ queryKey: ['reservation-tokens', reservationId] });
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      toast.success('Configuració actualitzada');
      onGuestsUpdate(); // Refrescar el modal pare
    },
    onError: (error) => {
      console.error('Error updating token config:', error);
      toast.error('Error actualitzant configuració');
    },
  });

  const formUrl = accessToken
    ? `${window.location.origin}/formulari/${accessToken.tokenHash}`
    : null;

  const copyLink = async () => {
    if (formUrl) {
      try {
        // Intentar usar l'API moderna del clipboard
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(formUrl);
        } else {
          // Fallback per a contextos no segurs (HTTP)
          const textArea = document.createElement('textarea');
          textArea.value = formUrl;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          textArea.remove();
        }
        setCopied(true);
        toast.success('Link copiat!');
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Error copiant al clipboard:', err);
        toast.error('No s\'ha pogut copiar. Copia manualment: ' + formUrl);
      }
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: 'default' | 'secondary' | 'outline' | 'destructive'; label: string }> = {
      PENDING: { variant: 'secondary', label: 'Pendent' },
      IN_USE: { variant: 'default', label: 'En ús' },
      PARTIAL: { variant: 'outline', label: 'Parcial' },
      COMPLETE: { variant: 'default', label: 'Complet' },
      ARCHIVED: { variant: 'secondary', label: 'Arxivat' },
      EXPIRED: { variant: 'destructive', label: 'Caducat' },
    };
    const config = variants[status] || { variant: 'outline', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const activeGuests = guests.filter((g) => g.isActive);
  const mossosGuests = guests.filter((g) => g.sendToMossos && g.isActive);

  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                <IconSettings className="h-5 w-5" />
                GuestPortal Config
              </span>
              <div className="flex items-center gap-2">
                {accessToken && getStatusBadge(accessToken.status)}
                {isOpen ? (
                  <IconChevronUp className="h-4 w-4" />
                ) : (
                  <IconChevronDown className="h-4 w-4" />
                )}
              </div>
            </CardTitle>
            <CardDescription>
              {activeGuests.length} viatgers actius · {mossosGuests.length} per Mossos
            </CardDescription>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="space-y-4">
            {/* Link del formulari */}
            {formUrl && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <IconLink className="h-4 w-4" />
                  Link del Formulari
                </Label>
                <div className="flex gap-2">
                  <Input value={formUrl} readOnly className="font-mono text-sm" />
                  <Button variant="outline" size="icon" onClick={copyLink}>
                    {copied ? (
                      <IconCheck className="h-4 w-4 text-green-600" />
                    ) : (
                      <IconCopy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Configuració del portal */}
            <Collapsible open={configOpen} onOpenChange={setConfigOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="outline" size="sm" className="w-full justify-between">
                  <span className="flex items-center gap-2">
                    <IconUsers className="h-4 w-4" />
                    Configuració Viatgers
                  </span>
                  {configOpen ? (
                    <IconChevronUp className="h-4 w-4" />
                  ) : (
                    <IconChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="minGuests">Mínim Viatgers</Label>
                    <Input
                      id="minGuests"
                      type="number"
                      min={0}
                      value={minGuests}
                      onChange={(e) => setMinGuests(parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maxGuests">Màxim Viatgers</Label>
                    <Input
                      id="maxGuests"
                      type="number"
                      min={0}
                      value={maxGuests ?? ''}
                      onChange={(e) =>
                        setMaxGuests(e.target.value ? parseInt(e.target.value) : null)
                      }
                      placeholder="Sense límit"
                    />
                  </div>
                </div>
                <Button
                  className="mt-4 w-full"
                  size="sm"
                  onClick={() => updateTokenConfigMutation.mutate({ minGuests, maxGuests })}
                  disabled={updateTokenConfigMutation.isPending || !accessToken}
                >
                  Guardar Configuració
                </Button>
              </CollapsibleContent>
            </Collapsible>

            {/* Llista de guests amb toggles */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <IconUsers className="h-4 w-4" />
                Flags per Viatger
              </Label>
              {guests.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No hi ha viatgers registrats
                </p>
              ) : (
                <div className="space-y-2">
                  {guests.map((guest) => {
                    const name =
                      guest.person?.firstName ||
                      guest.firstName ||
                      'Viatger sense nom';
                    const lastName = guest.person?.lastName || guest.lastName || '';
                    const fullName = `${name} ${lastName}`.trim();
                    const docNum = guest.person?.documentNumber;

                    return (
                      <div
                        key={guest.id}
                        className={`flex items-center justify-between p-3 border rounded-md ${
                          !guest.isActive ? 'bg-gray-100 opacity-60' : ''
                        }`}
                      >
                        <div className="flex-1">
                          <p className="font-medium text-sm">{fullName}</p>
                          {docNum && (
                            <p className="text-xs text-muted-foreground">{docNum}</p>
                          )}
                        </div>

                        <div className="flex items-center gap-4">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-2">
                                  <IconShieldCheck
                                    className={`h-4 w-4 ${
                                      guest.sendToMossos
                                        ? 'text-green-600'
                                        : 'text-gray-400'
                                    }`}
                                  />
                                  <Switch
                                    checked={guest.sendToMossos}
                                    onCheckedChange={(checked) =>
                                      updateGuestFlagsMutation.mutate({
                                        guestId: guest.id,
                                        flags: { sendToMossos: checked },
                                      })
                                    }
                                    disabled={updateGuestFlagsMutation.isPending}
                                  />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Enviar a Mossos</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-2">
                                  <IconUserOff
                                    className={`h-4 w-4 ${
                                      !guest.isActive
                                        ? 'text-red-600'
                                        : 'text-gray-400'
                                    }`}
                                  />
                                  <Switch
                                    checked={guest.isActive}
                                    onCheckedChange={(checked) =>
                                      updateGuestFlagsMutation.mutate({
                                        guestId: guest.id,
                                        flags: { isActive: checked },
                                      })
                                    }
                                    disabled={updateGuestFlagsMutation.isPending}
                                  />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Viatger Actiu</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Resum */}
            <div className="flex gap-2 pt-2">
              <Badge variant="outline">
                {activeGuests.length}/{totalGuests} actius
              </Badge>
              <Badge variant="outline" className="bg-green-50">
                {mossosGuests.length} per Mossos
              </Badge>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

export default GuestPortalSection;
